/* eslint-disable @typescript-eslint/restrict-template-expressions */
import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import InstructorReconfigureToggle from '../controls/InstructorReconfigureToggle';
import { useAppDispatch } from '../../hooks';
import { fetchEventDetails, selectEventBySlug } from '../../reducers/eventsSlice';
import EditableField from '../controls/EditableField';
import { getConfig } from '../../config/config-helper';
import { PageCenteredLayout } from '../layout/PageCenteredLayout';
import Grid3Layout from '../layout/Grid3Layout';
import SectionLayout from '../layout/SectionLayout';
import { type RootState } from '../../store';
import RejectButton from '../controls/EventRejectionButton';
import ApproveButton from '../controls/EventApprovalButton';
import dayjs from 'dayjs';
import AddInstructorButton from '../controls/EventAddInstructorButton';
import EventRemoveInstructorButton from '../controls/EventRemoveInstructorButton';
import { useToken } from '../../hooks/useToken';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { useNavigationTab } from '../../hooks/useNavigationTab';
import Button from '../controls/Button';
import EventAccountPoolAllocationBar from '../event/EventAccountPoolAllocationBar';
import EventHeader from '../event/EventHeader';
import NoMatchEvents from './NoMatchEvents';
import { UserIcon, WrenchScrewdriverIcon } from '@heroicons/react/24/outline';
import PoolStatus from '../event/PoolStatus';
import { dateFormat, dateTimeFormat } from '../../utilities/constants';
import { validateEventName } from '../../utilities/validation';

export default function EventDeploymentPage() {
  const dispatch = useAppDispatch();
  const { eventSlug } = useParams();
  const event = useSelector((state: RootState) => (eventSlug ? selectEventBySlug(state, eventSlug) : undefined));
  const { canPublish } = useCurrentUser();
  const role = canPublish ? 'admin' : 'consumer';
  const token = useToken();
  const { dataopsliveBaseUrl, eventGoApplicationBaseUrl } = getConfig();

  useEffect(() => {
    if (eventSlug) {
      dispatch(fetchEventDetails({ token, role, slug: eventSlug }))
        .then(() => {
          console.log('Event details fetched');
        })
        .catch((error: unknown) => {
          console.error('Failed to load event details:', error);
        });
    }
  }, [dispatch, token, role, eventSlug]);

  // Replace the useEffect for tab management with the custom hook
  useNavigationTab(
    event?.name
      ? {
          section: 'discover/event-deployments',
          label: event.name,
          href: `/event-deployments/${event.slug}`,
          status: 'loaded',
        }
      : null,
    [event?.name, event?.slug],
  );

  if (!event) {
    return <NoMatchEvents />;
  }

  const formattedStartDate = dayjs(event.start_datetime).format(dateFormat);
  const formattedStartDateTime = dayjs(event.start_datetime).format(dateTimeFormat);
  const formattedEndDateTime = dayjs(event.end_datetime).format(dateTimeFormat);
  const formattedDecommissionDateTime = dayjs(event.decommission_datetime).format(dateTimeFormat);
  const formattedBuildDateTime = dayjs(event.build_datetime).format(dateTimeFormat);
  const poolStatus = event.account_pool?.status ?? 'No status available';
  const totalReadyAccounts = event.account_pool?.total_ready_child_accounts ?? 0;
  const allocatedAccounts = event.account_pool?.total_allocated_child_accounts ?? 0;
  const assignedPercentage = totalReadyAccounts === 0 ? 0 : (allocatedAccounts / totalReadyAccounts) * 100;
  const instructors = event.instructors || [];

  const getApprovalStatusStyles = (approvalStatus: string, poolStatus?: string) => {
    const isDecommissioned =
      poolStatus && ['pending-decommissioning', 'decommissioning', 'decommissioned'].includes(poolStatus.toLowerCase());

    switch (approvalStatus) {
      case 'APPROVED':
        if (isDecommissioned) {
          return 'border-l-4 border-black';
        }

        if (poolStatus && poolStatus.toLowerCase() === 'ready') {
          return 'border-l-4 border-green-600';
        }
        return 'border-l-4 border-green-600';

      case 'PENDING':
        return 'border-l-4 border-yellow-600';

      case 'REJECTED':
        return 'border-l-4 border-red-600';

      default:
        return 'border-l-4 border-dataops-primary-light-blue';
    }
  };

  const assignedPoolSize = event.account_pool?.pool_size ?? event.initial_pool_size ?? 'No size available';

  // MARK: Validation functions

  const validatePoolSize = (currentValue: string | number, newValue: string | number) => {
    let isValid = true;
    let error = null;
    const currentValueNumber = Number(currentValue);
    const newValueNumber = Number(newValue);
    if (newValueNumber < currentValueNumber) {
      error = 'Pool size cannot be reduced';
      isValid = false;
    } else if (newValueNumber > currentValueNumber + 200) {
      error = 'Pool size cannot be increased by more than 200 accounts';
      isValid = false;
    }
    return { isValid, error };
  };

  const validateBuildDate = (currentValue: string | number, newValue: string | number) => {
    let isValid = true;
    let error = null;
    const newValueDate = dayjs(newValue);
    if (newValueDate.isAfter(dayjs(event.start_datetime))) {
      error = 'Build date cannot be after the start date';
      isValid = false;
    }
    return { isValid, error };
  };

  const validateStartDate = (currentValue: string | number, newValue: string | number) => {
    let isValid = true;
    let error = null;
    const newValueDate = dayjs(newValue);
    if (newValueDate.isBefore(dayjs(event.build_datetime)) || newValueDate.isAfter(dayjs(event.end_datetime))) {
      error = 'Start date must be between build date and end date';
      isValid = false;
    }
    return { isValid, error };
  };

  const validateEndDate = (currentValue: string | number, newValue: string | number) => {
    let isValid = true;
    let error = null;
    const newValueDate = dayjs(newValue);
    if (
      newValueDate.isBefore(dayjs(event.start_datetime)) ||
      newValueDate.isAfter(dayjs(event.decommission_datetime))
    ) {
      error = 'End date must be between start date and decommission date';
      isValid = false;
    }
    return { isValid, error };
  };

  const validateDecommissionDate = (currentValue: string | number, newValue: string | number) => {
    let isValid = true;
    let error = null;
    const newValueDate = dayjs(newValue);
    if (newValueDate.isBefore(dayjs(event.end_datetime).add(1, 'day'))) {
      error = 'Decommission date must be at least 1 day after the after end date';
      isValid = false;
    }
    return { isValid, error };
  };

  // MARK: Return
  return (
    <div className="relative" key={event.slug}>
      <main>
        <EventHeader
          eventName={event.name}
          eventSlug={event.slug}
          eventLocation={event.location}
          formattedStartDate={formattedStartDate}
          activeTab="detail"
        />

        <PageCenteredLayout>
          <Grid3Layout>
            <SectionLayout
              label=""
              className={`md:col-span-2 ${getApprovalStatusStyles(event.approval_status, event.account_pool?.status)}`}
            >
              {event.approval_status === 'PENDING' && (
                <div className="flex items-center justify-between h-full">
                  <p className="text-dataops-yale-blue my-auto">Pending Approval</p>
                  {canPublish && (
                    <div className="flex flex-col gap-2">
                      <ApproveButton eventSlug={event.slug} size="mediumPlus" />
                      <RejectButton eventSlug={event.slug} size="mediumPlus" />
                    </div>
                  )}
                </div>
              )}
              {event.approval_status === 'APPROVED' && (
                <div className="flex items-center h-full">
                  <p className="text-dataops-yale-blue my-auto">
                    Approved on {event.approval_date && dayjs(event.approval_date).format(dateFormat)}
                  </p>
                </div>
              )}
              {event.approval_status === 'REJECTED' && (
                <div className="flex items-center h-full">
                  <p className="text-dataops-yale-blue my-auto">Rejected: {event.rejection_reason}</p>
                </div>
              )}
            </SectionLayout>
            <SectionLayout label="Actions" className="bg-dataops-platinum-gray/40 md:col-span-1 md:col-start-3">
              <div className="space-y-1">
                <div className="flex flex-col">
                  <Button
                    size="large"
                    intent="light"
                    onClick={() => {
                      window.open(`${dataopsliveBaseUrl}/${event.project}`, '_blank', 'noopener,noreferrer');
                    }}
                    className="w-full"
                  >
                    <WrenchScrewdriverIcon className="w-4 h-4 mr-1" /> View Configuration Project
                  </Button>
                </div>
                <div className="flex flex-col">
                  <Button
                    size="large"
                    intent="primary"
                    onClick={() => {
                      window.open(`${eventGoApplicationBaseUrl}/${event.slug}`, '_blank', 'noopener,noreferrer');
                    }}
                    className="w-full"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-4 h-4 mr-1"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="m3.75 13.5 10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75Z"
                      />
                    </svg>
                    Go to Event Signup
                  </Button>
                </div>
              </div>
            </SectionLayout>
            <SectionLayout label="Event Review" className="md:col-span-2">
              <div className="grid grid-cols-4 gap-1 text-sm/6">
                <div className="col-span-1">Requested By:</div>
                <div className="col-span-3">{event.requestor}</div>
                <div className="col-span-1">Organization Account:</div>
                <div className="col-span-3">{event.organization_account}</div>
                <div className="col-span-1">Account Pool Size:</div>
                <div className="col-span-3">
                  <EditableField
                    value={assignedPoolSize}
                    eventSlug={event.slug}
                    fieldName="pool_size"
                    token={token}
                    fieldType="number"
                    onUpdate={() => {
                      void dispatch(fetchEventDetails({ token, role, slug: event.slug }));
                    }}
                    validate={validatePoolSize}
                    confirmationMessage="Are you sure you want to change the account pool size?"
                    isAdmin={canPublish}
                  />
                </div>
                <div className="col-span-1">Configuration Project:</div>
                <div className="col-span-3">
                  <a
                    href={`${dataopsliveBaseUrl}/${event.project}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-1 text-blue-600 hover:underline"
                  >
                    {event.project}
                  </a>
                </div>
                <div className="col-span-1">Account Region:</div>
                <div className="col-span-3">{event.snowflake_account_region_group}</div>
                <div className="col-span-1">Account Edition:</div>
                <div className="col-span-3">{event.snowflake_account_edition}</div>
              </div>
            </SectionLayout>
            <SectionLayout label="Manage Instructors" className="md:col-span-1">
              <div className="flex flex-col h-full">
                <AddInstructorButton eventSlug={event.slug} size="medium" className="" />
                <div className="mt-4 max-h-32 overflow-y-auto pr-2">
                  <div className="space-y-2">
                    {instructors.map((instructorEmail, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <div className="flex items-center gap-2 flex-grow">
                          <UserIcon className="h-4 w-4 flex-shrink-0 text-dataops-yale-blue" />
                          <span className="text-dataops-yale-blue text-xs truncate">{instructorEmail}</span>
                        </div>
                        <EventRemoveInstructorButton
                          eventSlug={event.slug}
                          instructorEmail={instructorEmail}
                          className="flex-shrink-0 ml-1"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Instructor Reconfigure Toggle - only visible to admins */}
                {canPublish && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <InstructorReconfigureToggle
                      eventSlug={event.slug}
                      enabled={event.instructor_reconfigure || undefined}
                      token={token}
                    />
                  </div>
                )}
              </div>
            </SectionLayout>
            <SectionLayout label="Event Detail" className="md:col-span-2">
              <div className="grid grid-cols-4 gap-1 text-sm/6">
                <div className="col-span-1">Event Name:</div>
                <div className="col-span-3">
                  <EditableField
                    value={event.name}
                    eventSlug={event.slug}
                    fieldName="name"
                    token={token}
                    onUpdate={() => {
                      void dispatch(fetchEventDetails({ token, role, slug: event.slug }));
                    }}
                    isAdmin={canPublish}
                    validate={(_original, newValue) => {
                      const error = validateEventName(String(newValue));
                      return error ? { isValid: false, error } : { isValid: true, error: null };
                    }}
                  />
                </div>
                <div className="col-span-1">Event Slug:</div>
                <div className="col-span-3">
                  <a
                    href={`${eventGoApplicationBaseUrl}/${event.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    {event.slug}
                  </a>
                </div>
                <div className="col-span-1">Location:</div>
                <div className="col-span-3">
                  <EditableField
                    value={event.location}
                    eventSlug={event.slug}
                    fieldName="location"
                    token={token}
                    onUpdate={() => {
                      void dispatch(fetchEventDetails({ token, role, slug: event.slug }));
                    }}
                    isAdmin={canPublish}
                  />
                </div>
                <div className="col-span-1">Build Date:</div>
                <div className="col-span-3">
                  <EditableField
                    value={formattedBuildDateTime}
                    eventSlug={event.slug}
                    fieldName="build_date"
                    token={token}
                    isDate={true}
                    fieldType="datetime"
                    onUpdate={() => {
                      void dispatch(fetchEventDetails({ token, role, slug: event.slug }));
                    }}
                    validate={validateBuildDate}
                    isAdmin={canPublish}
                  />
                </div>
                <div className="col-span-1">Start Date:</div>
                <div className="col-span-3">
                  <EditableField
                    value={formattedStartDateTime}
                    eventSlug={event.slug}
                    fieldName="start_date"
                    token={token}
                    isDate={true}
                    fieldType="datetime"
                    onUpdate={() => {
                      void dispatch(fetchEventDetails({ token, role, slug: event.slug }));
                    }}
                    validate={validateStartDate}
                    isAdmin={canPublish}
                  />
                </div>
                <div className="col-span-1">End Date:</div>
                <div className="col-span-3">
                  <EditableField
                    value={formattedEndDateTime}
                    eventSlug={event.slug}
                    fieldName="end_date"
                    token={token}
                    isDate={true}
                    fieldType="datetime"
                    onUpdate={() => {
                      void dispatch(fetchEventDetails({ token, role, slug: event.slug }));
                    }}
                    validate={validateEndDate}
                    isAdmin={canPublish}
                  />
                </div>
                <div className="col-span-1">Decommission Date:</div>
                <div className="col-span-3">
                  <EditableField
                    value={formattedDecommissionDateTime}
                    eventSlug={event.slug}
                    fieldName="decommission_date"
                    token={token}
                    isDate={true}
                    fieldType="datetime"
                    onUpdate={() => {
                      void dispatch(fetchEventDetails({ token, role, slug: event.slug }));
                    }}
                    validate={validateDecommissionDate}
                    isAdmin={canPublish}
                  />
                </div>
                <div className="col-span-1">Allowed Email Domains:</div>
                <div className="col-span-3">
                  <EditableField
                    value={
                      event.allowed_email_domains && event.allowed_email_domains.length > 0
                        ? event.allowed_email_domains.join(', ')
                        : 'All domains allowed'
                    }
                    eventSlug={event.slug}
                    fieldName="allowed_email_domains"
                    token={token}
                    onUpdate={() => {
                      void dispatch(fetchEventDetails({ token, role, slug: event.slug }));
                    }}
                    parseValue={(value: string | number) => {
                      const stringValue = value.toString().trim();
                      // If empty or "All domains allowed", return empty array
                      if (!stringValue || stringValue === 'All domains allowed') {
                        return [];
                      }
                      // Parse comma-separated domains
                      return stringValue
                        .split(',')
                        .map((d) => d.trim())
                        .filter((d) => d.length > 0);
                    }}
                    isAdmin={canPublish}
                  />
                </div>
              </div>
            </SectionLayout>
            <SectionLayout label="Account Pool" className="md:col-span-1">
              <div className="flex flex-col h-full">
                <EventAccountPoolAllocationBar allocatedPercentage={assignedPercentage} />
                <p className="text-dataops-yale-blue text-sm/6">
                  Status: <PoolStatus status={poolStatus} />
                </p>
                <p className="text-dataops-yale-blue text-sm/6">Accounts Ready: {totalReadyAccounts}</p>
                <p className="text-dataops-yale-blue text-sm/6">Accounts Assigned to user: {allocatedAccounts}</p>
              </div>
            </SectionLayout>
          </Grid3Layout>

          {/* Instructions Section */}
          <div className="mt-8">
            <SectionLayout label="Instructions" className="bg-dataops-platinum-gray/40">
              <div>
                <EditableField
                  value={event.instructions || 'No instructions available.'}
                  eventSlug={event.slug}
                  fieldName="instructions"
                  token={token}
                  isMarkdown={true}
                  markdownEventName={event.name}
                  onUpdate={() => {
                    void dispatch(fetchEventDetails({ token, role, slug: event.slug }));
                  }}
                  isAdmin={canPublish}
                />
              </div>
            </SectionLayout>
          </div>
        </PageCenteredLayout>
      </main>
    </div>
  );
}
